import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ChevronDown,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Send,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { EProjectStatus } from '@/enums';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { useMutateApprovePublishingProject } from '@/reactQuery/post';
import useProjectRequiredFieldsStore from '@/store/ProjectRequiredFieldsStore';
import getHTMLTextOnPublishButtonHover from '@/utils/getHTMLTextOnPublishButtonHover';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import PublishProjectModal from '../Modals/PublishProjectModal';
import CompleteProjectModal from '../Modals/CompleteProjectModal';
import RequestPublishingModal from '../Modals/RequestPublishingModal';
import UnpublishProjectModal from '../Modals/UnpublishProjectModal';
import RepublishProjectModal from '../Modals/RepublishProjectModal';

type ModalState = 'publish' | 'complete' | 'request' | 'unpublish' | 'republish' | null;

interface StatusConfig {
  label: string;
  bg: string;
  text: string;
  border?: string;
  dot?: boolean;
  icon?: React.ElementType;
}

const STATUS_CONFIG: Record<EProjectStatus, StatusConfig> = {
  [EProjectStatus.DRAFT]: {
    label: 'statusDraft',
    bg: 'bg-neutral-100',
    text: 'text-neutral-600',
  },
  [EProjectStatus.READY]: {
    label: 'statusReady',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border border-blue-200',
  },
  [EProjectStatus.WAITING_APPROVAL]: {
    label: 'statusAwaitingApproval',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border border-amber-200',
  },
  [EProjectStatus.REQUEST_APPROVED]: {
    label: 'statusApproved',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border border-emerald-200',
  },
  [EProjectStatus.PUBLISHED]: {
    label: 'statusLive',
    bg: 'bg-emerald-600',
    text: 'text-white',
    dot: true,
  },
  [EProjectStatus.COMPLETED]: {
    label: 'statusCompleted',
    bg: 'bg-neutral-100',
    text: 'text-neutral-600',
    icon: Check,
  },
};

function ProjectStatusMenu() {
  const {
    project_id: projectId,
    workspace_id: workspaceId,
    organization_id: organizationId,
  } = useParams();
  const { lang } = useTranslation();

  const { emptyFieldsLabels } = useProjectRequiredFieldsStore();
  const [publishTooltipText, setPublishTooltipText] = useState<string>('');
  const [openModal, setOpenModal] = useState<ModalState>(null);

  const {
    isUserAdmin: isUserAdminOrg,
    isUserEditor: isUserEditorOrg,
    isUserOwner: isUserOwnerOrg,
  } = useOrganizationRoles();
  const { currentProject: singleProjectData } = useCurrentProject();
  const { isUserViewer: isUserViewerWorkspace } = useWorkspaceRoles();

  // statuses
  const status = singleProjectData?.status as EProjectStatus | undefined;
  const isStatusDraft = status === EProjectStatus.DRAFT;
  const isStatusReady = status === EProjectStatus.READY;
  const isStatusRequestApproved = status === EProjectStatus.REQUEST_APPROVED;
  const isStatusWaitingApproval = status === EProjectStatus.WAITING_APPROVAL;
  const isStatusPublished = status === EProjectStatus.PUBLISHED;
  const isStatusCompleted = status === EProjectStatus.COMPLETED;

  // role groups
  const IS_ORG_OWNER_OR_ADMIN = isUserOwnerOrg || isUserAdminOrg;
  const HAS_ORG_ROLE = IS_ORG_OWNER_OR_ADMIN || isUserEditorOrg;

  const hasEmptyFields = emptyFieldsLabels.length > 0;

  const { mutateAsync: mutateAsyncApprovePublishing } = useMutateApprovePublishingProject(
    organizationId,
    workspaceId,
  );

  const handleApprovePublishing = async () => {
    try {
      await mutateAsyncApprovePublishing(projectId as any);
      toast.success(lang.get('msg.projectSuccessfullyApproved'));
    } catch {
      toast.error(lang.get('msg.error') || 'Error. Please try again.');
    }
  };

  useEffect(() => {
    setPublishTooltipText(getHTMLTextOnPublishButtonHover(emptyFieldsLabels, lang));
  }, [emptyFieldsLabels, lang]);

  if (!status || !STATUS_CONFIG[status]) return null;

  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  // Format published date
  const publishedAt = (singleProjectData as { publishedAt?: string })?.publishedAt;
  const publishedDate = publishedAt
    ? new Date(publishedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const closeModal = () => setOpenModal(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-sm font-medium transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-neutral-400',
              config.bg,
              config.text,
              config.border,
            )}
          >
            {config.dot && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
            )}
            {StatusIcon && <StatusIcon size={13} strokeWidth={2.5} />}
            {lang.get(`msg.${config.label}`)}
            <ChevronDown size={13} strokeWidth={2.5} className="opacity-70" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          {/* DRAFT */}
          {isStatusDraft && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.statusDraft')}
                <p className="text-xs font-normal text-neutral-400 mt-0.5">
                  {lang.get('msg.fillRequiredFields')}
                </p>
              </DropdownMenuLabel>

              {hasEmptyFields && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <div className="flex items-start gap-2 text-amber-700 bg-amber-50 rounded-md p-2 text-xs">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium mb-1">{lang.get('msg.missingRequiredFields')}</p>
                        <ul className="list-disc pl-3 space-y-0.5">
                          {emptyFieldsLabels.slice(0, 5).map((label) => (
                            <li key={label}>{lang.get(`msg.${label}`) || label}</li>
                          ))}
                          {emptyFieldsLabels.length > 5 && (
                            <li>+{emptyFieldsLabels.length - 5} {lang.get('msg.nMore')}</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <DropdownMenuSeparator />

              {IS_ORG_OWNER_OR_ADMIN && (
                <DropdownMenuItem
                  disabled={hasEmptyFields || isUserViewerWorkspace}
                  onClick={() => setOpenModal('publish')}
                  className="gap-2"
                  title={hasEmptyFields ? publishTooltipText : undefined}
                >
                  <Send size={14} />
                  {lang.get('msg.publish')}
                </DropdownMenuItem>
              )}

              {isUserEditorOrg && (
                <DropdownMenuItem
                  disabled={hasEmptyFields || isUserViewerWorkspace}
                  onClick={() => setOpenModal('request')}
                  className="gap-2"
                  title={hasEmptyFields ? publishTooltipText : undefined}
                >
                  <Send size={14} />
                  {lang.get('msg.requestToPublish')}
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* READY */}
          {isStatusReady && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.readyToPublish')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {IS_ORG_OWNER_OR_ADMIN && (
                <DropdownMenuItem
                  disabled={isUserViewerWorkspace}
                  onClick={() => setOpenModal('publish')}
                  className="gap-2"
                >
                  <Send size={14} />
                  {lang.get('msg.publish')}
                </DropdownMenuItem>
              )}

              {isUserEditorOrg && (
                <DropdownMenuItem
                  disabled={isUserViewerWorkspace}
                  onClick={() => setOpenModal('request')}
                  className="gap-2"
                >
                  <Send size={14} />
                  {lang.get('msg.requestToPublish')}
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* WAITING_APPROVAL */}
          {isStatusWaitingApproval && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.awaitingAdminApproval')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {IS_ORG_OWNER_OR_ADMIN && (
                <DropdownMenuItem
                  disabled={isUserViewerWorkspace}
                  onClick={handleApprovePublishing}
                  className="gap-2"
                >
                  <Send size={14} />
                  {lang.get('msg.approvePublishing')}
                </DropdownMenuItem>
              )}

              {isUserEditorOrg && (
                <DropdownMenuItem
                  disabled={isUserViewerWorkspace}
                  onClick={() => setOpenModal('request')}
                  className="gap-2"
                >
                  <Send size={14} />
                  {lang.get('msg.requestApprovalAgain') || 'Request Again'}
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* REQUEST_APPROVED */}
          {isStatusRequestApproved && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.requestApproved')}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                disabled={isUserViewerWorkspace}
                onClick={() => setOpenModal('publish')}
                className="gap-2"
              >
                <Send size={14} />
                {lang.get('msg.publishNow')}
              </DropdownMenuItem>
            </>
          )}

          {/* PUBLISHED */}
          {isStatusPublished && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.projectPublished')}
                {publishedDate && (
                  <p className="text-xs font-normal text-neutral-400 mt-0.5">
                    {lang.get('msg.since')} {publishedDate}
                  </p>
                )}
              </DropdownMenuLabel>

              {HAS_ORG_ROLE && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={isUserViewerWorkspace}
                    onClick={() => setOpenModal('complete')}
                    className="gap-2"
                  >
                    <CheckCircle2 size={14} />
                    {lang.get('msg.complete')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={isUserViewerWorkspace}
                    onClick={() => setOpenModal('unpublish')}
                    className="gap-2 text-neutral-500 focus:text-neutral-700"
                  >
                    <XCircle size={14} />
                    {lang.get('msg.unpublish')}...
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}

          {/* COMPLETED */}
          {isStatusCompleted && (
            <>
              <DropdownMenuLabel className="font-semibold text-neutral-700">
                {lang.get('msg.completed')}
              </DropdownMenuLabel>

              {HAS_ORG_ROLE && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={isUserViewerWorkspace}
                    onClick={() => setOpenModal('republish')}
                    className="gap-2"
                  >
                    <RotateCcw size={14} />
                    {lang.get('msg.republish')}
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modals */}
      <PublishProjectModal
        isOpen={openModal === 'publish'}
        onClose={closeModal}
        projectTitle={singleProjectData?.title}
        projectId={projectId}
        isPublishRequestApproved={isStatusRequestApproved}
      />

      <CompleteProjectModal isOpen={openModal === 'complete'} onClose={closeModal} />

      <RequestPublishingModal isOpen={openModal === 'request'} onClose={closeModal} />

      {(IS_ORG_OWNER_OR_ADMIN || isUserEditorOrg) && (
        <UnpublishProjectModal
          projectId={projectId || ''}
          projectTitle={singleProjectData?.title || ''}
          organizationId={organizationId}
          workspaceId={workspaceId}
          isOpen={openModal === 'unpublish'}
          onClose={closeModal}
        />
      )}

      {(IS_ORG_OWNER_OR_ADMIN || isUserEditorOrg) && (
        <RepublishProjectModal
          projectId={projectId || ''}
          projectTitle={singleProjectData?.title || ''}
          organizationId={organizationId}
          workspaceId={workspaceId}
          isOpen={openModal === 'republish'}
          onClose={closeModal}
        />
      )}
    </>
  );
}

export default ProjectStatusMenu;
